import TelegramButton, { TelegramIcon } from "./TelegramButton";
import { Component, Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type SyntheticEvent } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { PRODUCT_NAME, TELEGRAM_BOT_HANDLE, TWITTER_URL, telegramBotUrl } from "../lib/constants";
import { useT } from "../lib/i18n";
import { landingCopy } from "../lib/landing-copy";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";
import Logo from "./Logo";
import { PanelSkeleton } from "./AsyncPanel";

function XIcon() {
  return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
}

class PageBoundary extends Component<{ children: ReactNode; label: string; retry: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <div className="a1-container a1-data-state" role="alert"><p>{this.props.label}</p><button className="a1-button" onClick={() => window.location.reload()}>{this.props.retry}</button></div>;
    return this.props.children;
  }
}

export default function Layout({ routePhase = "idle", onRouteIntent }: { routePhase?: string; onRouteIntent?: (pathname: string) => unknown }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const { t, lang } = useT();
  const c = landingCopy[lang];
  const { pathname, hash, key: locationKey } = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);
  const navItems = [{ to: "/how-it-works", label: t.nav.how_it_works }, { to: "/tokens", label: t.nav.tokens }, { to: "/strategies", label: t.nav.strategy_lab }];

  useEffect(() => {
    setShowMobileCta(false);
    if (pathname !== "/") return;
    const hero = document.querySelector(".a1-hero");
    const final = document.querySelector(".a1-final");
    const footer = document.querySelector(".a1-footer");
    if (!hero || !final || !footer) return;
    let pastHero = false;
    let atEnd = false;
    let atFooter = false;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.target === hero) pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        if (entry.target === final) atEnd = entry.isIntersecting;
        if (entry.target === footer) atFooter = entry.isIntersecting;
      }
      setShowMobileCta(pastHero && !atEnd && !atFooter);
    });
    [hero, final, footer].forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [pathname]);

  useLayoutEffect(() => {
    setMobileOpen(false);
    if (hash) {
      const focusAnchor = () => {
        const target = document.getElementById(hash.slice(1));
        if (!target) return false;
        target.tabIndex = -1;
        target.scrollIntoView();
        target.focus({ preventScroll: true });
        return true;
      };
      if (focusAnchor()) return;
      const frame = requestAnimationFrame(focusAnchor);
      return () => cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    document.getElementById("main-content")?.focus({ preventScroll: true });
  }, [pathname, hash, locationKey]);

  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setMobileOpen(false); menuButton.current?.focus(); }
    };
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false); };
    document.addEventListener("keydown", closeOnEscape);
    desktop.addEventListener("change", closeOnDesktop);
    return () => { document.removeEventListener("keydown", closeOnEscape); desktop.removeEventListener("change", closeOnDesktop); };
  }, [mobileOpen]);

  useEffect(() => {
    const routeTitles: Record<string, string> = { "/": c.footerTag, "/how-it-works": t.nav.how_it_works, "/tokens": t.nav.tokens, "/strategies": t.nav.strategy_lab };
    const isKnown = pathname in routeTitles;
    const title = `${PRODUCT_NAME} | ${routeTitles[pathname] ?? c.notFound}`;
    const descriptions: Record<string, string> = { "/": c.intro, "/how-it-works": t.how.hero_sub, "/tokens": t.tokens.page_sub, "/strategies": t.lab.page_sub };
    const description = descriptions[pathname] ?? c.notFoundSub;
    const canonical = `https://alphahedge.kuzeydurden.com${isKnown ? pathname : "/"}`;
    document.title = title;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", canonical);
    for (const [selector, value] of [
      ['meta[name="description"]', description], ['meta[property="og:title"]', title], ['meta[name="twitter:title"]', title], ['meta[property="og:description"]', description], ['meta[name="twitter:description"]', description], ['meta[property="og:url"]', canonical],
    ]) document.querySelector(selector)?.setAttribute("content", value);
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.content = isKnown ? "index,follow" : "noindex,follow";
  }, [pathname, lang, c, t]);

  const warmLink = (event: SyntheticEvent<HTMLDivElement>) => {
    const link = (event.target as Element).closest?.("a");
    if (link instanceof HTMLAnchorElement && link.origin === window.location.origin) onRouteIntent?.(link.pathname);
  };

  return <div className="a1-site" data-route-phase={routePhase} onPointerOverCapture={warmLink} onFocusCapture={warmLink}>
    <a className="a1-skip" href="#main-content">{c.skip}</a>
    <header className="a1-header"><div className="a1-container a1-header-inner">
      <Link to="/" className="a1-wordmark" aria-label={`${PRODUCT_NAME} ${t.nav.home}`}><Logo size={28}/><span>{PRODUCT_NAME}</span></Link>
      <nav className="a1-desktop-nav" aria-label={c.navigation}>{navItems.map(item => <NavLink key={item.to} to={item.to}>{item.label}</NavLink>)}<Link to="/#plans">{c.pricing}</Link></nav>
      <div className="a1-header-actions"><LanguageSwitcher/><ThemeToggle/><TelegramButton source="header" className="a1-header-cta">{c.start}</TelegramButton><button ref={menuButton} type="button" className="a1-icon-button a1-menu-button" aria-label={t.nav.toggle_menu} aria-expanded={mobileOpen} aria-controls="mobile-navigation" onClick={() => setMobileOpen(v => !v)}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">{mobileOpen ? <path d="m6 6 12 12M6 18 18 6"/> : <path d="M4 7h16M4 12h16M4 17h16"/>}</svg></button></div>
    </div>{mobileOpen && <nav className="a1-mobile-nav a1-container" id="mobile-navigation" aria-label={c.navigation}>{[{ to: "/", label: t.nav.home }, ...navItems, { to: "/#plans", label: c.pricing }].map(item => <NavLink end key={item.to} to={item.to} onClick={() => setMobileOpen(false)}>{item.label}<span aria-hidden="true">↗</span></NavLink>)}<TelegramButton source="menu" onClick={() => setMobileOpen(false)}>{c.start}</TelegramButton></nav>}</header>
    <main className="a1-main" id="main-content" tabIndex={-1} aria-busy={routePhase !== "idle"}><PageBoundary key={pathname} label={lang === "tr" ? "Sayfa yüklenemedi. Lütfen tekrar dene." : "This page could not load. Please try again."} retry={t.misc.retry}><Suspense fallback={<div className="a1-container a1-route-skeleton" role="status"><p>{lang === "tr" ? "Sayfa hazırlanıyor…" : "Preparing your page…"}</p><PanelSkeleton rows={9}/></div>}><div key={pathname} className="a1-page-content"><Outlet/></div></Suspense></PageBoundary></main>
    {showMobileCta && !mobileOpen && <aside className="a1-mobile-cta"><span>{c.mobileCtaNote}</span><TelegramButton source="sticky">{c.start}</TelegramButton></aside>}
    <footer className="a1-footer"><div className="a1-container">
      <div className="a1-footer-top">
        <div><Link to="/" className="a1-wordmark"><Logo size={26}/><span>{PRODUCT_NAME}</span></Link><p>{c.footerTag}</p><p className="a1-footer-copy">© {new Date().getFullYear()} {PRODUCT_NAME}</p></div>
        <nav aria-label={c.footerNav}>{navItems.map(item => <Link key={item.to} to={item.to}>{item.label}</Link>)}<Link to="/#plans">{c.pricing}</Link><a href={TWITTER_URL} target="_blank" rel="noopener noreferrer" aria-label={t.nav.follow_x}><XIcon/> X</a><a href={telegramBotUrl("footer")} target="_blank" rel="noopener noreferrer"><TelegramIcon/>Telegram</a></nav>
      </div>
      <div className="a1-footer-sign"><p>{c.footerNote}</p><a href={telegramBotUrl("footer")} target="_blank" rel="noopener noreferrer">{TELEGRAM_BOT_HANDLE}</a></div>
    </div></footer>
  </div>;
}
